import { IMemberModel, IMemberInvitation, IMembershipModel, IMembershipContainer, IOrganization } from '~/interfaces/organizationInterfaces';
import Folder from "../../formability/document_management/models/folder";
import Folderable from "../../formability/document_management/models/mixins/folderable";
import Group from "../../formability/organization_management/models/group";
import Membership from "../../formability/organization_management/models/membership";
import PaymentGateway from "../../formability/organization_management/models/paymentGateway";
import ImageUpload from "../../formability/organization_settings/models/imageUpload";
import { DocumentType } from "../enums/documentType";
import { OrganizationRoleCapability } from "../enums/roles/capabilities/organizationRoleCapability";
import { RoleType } from "../enums/roles/roleType";
import { RouteHelper } from "../helpers/routeHelper";
import { TFolderDocument, TOrganizationDocument } from "../interfaces/organizationDocumentInterfaces";
import IProvider from "../interfaces/provider";
import IRepo from "../interfaces/repo";
import {IDocumentRole, IOrganizationRole, IPeerDocumentRole, IPortalRole} from "../interfaces/roleInterfaces";
import Capable from "../mixins/capable";
import Provider from "../providers/new/provider";
import Repo from "../state/modules/repo";
import store from "../state/store";
import EntityType from "./entityTypes/entityType";
import ExternalConnection from "./externalConnections/externalConnection";
import ModelCollection from "./modelCollection";
import { RelationType } from "./relationType";
import Role from "./role";
import FormTemplate from "./templates/formTemplate";
import UserType from "~/models/userTypes/userType";
import QuickLinkFolder from "../../formability/organization_management/models/quickLinkFolder";
import {DefaultEmailStyle, DefaultEmailStyleName} from "~/enums/defaultEmailStyle";

export default class Organization extends Capable(Folderable) implements IOrganization, IMembershipContainer {
  name: string;
  id: string;
  description: string;
  is_editable: boolean;
  is_workflow_publish_approval_required: boolean;
  time_zone: string = 'America/New_York';
  memberships: IMembershipModel[] = [];
  groups: Group[] = [];
  image_uploads: ImageUpload[] = [];
  payment_gateways: PaymentGateway[] = [];
  isLoadingPortals: boolean = false;
  except: string[] = ['forms', 'licenses'];
  roles: Role[] = [];
  organization_owner_role_id: string;
  group_owner_role_id: string;
  document_owner_role_id: string;
  portal_owner_role_id: string;
  app_owner_role_id: string;
  templates: ModelCollection<FormTemplate> = new ModelCollection<FormTemplate>();
  externalConnections: ExternalConnection[] = [];
  subdomain: string;
  entity_types: EntityType[] = [];
  user_types: UserType[] = [];
  quick_link_folders: QuickLinkFolder[] = [];
  default_email_style: number;
  organization_email_style_options: Array<{label: string, value: DefaultEmailStyle }>

  static repo: IRepo<Organization, IProvider<Organization>> = new Repo<Organization, IProvider<Organization>>('organizations', Organization, {}, new Provider(Organization, 'organization', 'organizations'));

  static get displayName(): string {
    return 'Organization';
  }

  get EMBEDDED_MODELS(): {[key: string]: {type: any, relationType: RelationType}} {
    return {
      'memberships': {type: Membership, relationType: RelationType.array},
      'groups': {type: Group, relationType: RelationType.array},
      'folders': {type: Folder, relationType: RelationType.array},
      'roles': {type: Role, relationType: RelationType.array},
      'image_uploads': {type: ImageUpload, relationType: RelationType.array}
    };
  }

  emailStyleOptions(includeDefault: boolean = true): Array<{ label: string, value: DefaultEmailStyle }> {
    const options = this.organization_email_style_options
    if (includeDefault){
      return [{label: `${DefaultEmailStyleName[DefaultEmailStyle.Default]} (${DefaultEmailStyle[this.default_email_style]})`, value: DefaultEmailStyle.Default  }, ...options]
    }
    else{
      return options
    }
  }

  find(id: string): TFolderDocument {
    return super.find(id) || this.findRole(id);
  }

  get organization() {
    return this;
  }

  get archiveFolder(): Folder {
    return this.folders.find(f => f.name === 'Archive' || f.name === 'Trash');
  }

  get rootFolder(): Folder {
    return this.folders.find(f => f.name !== 'Archive' && f.name !== 'Trash');
  }

  get hasGroups(): boolean {
    return this.groups.length > 0;
  }

  get hasMemberships(): boolean {
    return this.memberships.length > 0;
  }

  findMembershipByEmail(email: string): IMembershipModel {
    return this.memberships.find(m => {
      const emailSource = m.member || m.invitation;
      return emailSource.email === email;
    });
  }

  findGroup(groupId: string): Group {
    return this.groups.find(g => g.id === groupId);
  }

  members(): (IMemberModel | IMemberInvitation)[] {
    return this.memberships.map(membership => membership.member || membership.invitation);
  }

  memberEmails(): string[] {
    return this.getEmails(this.members());
  }

  pendingMemberships(): IMembershipModel[] {
    return this.memberships.filter(membership => membership.active === false);
  }

  pendingMembers(): IMemberModel[] {
    return this.pendingMemberships().map(membership => membership.member || membership.invitation);
  }

  pendingMemberEmails(): string[] {
    return this.getEmails(this.pendingMembers());
  }

  fullMemberships(): IMembershipModel[] {
    return this.memberships.filter(membership => membership.active === true);
  }

  fullMembers(): IMemberModel[] {
    return this.fullMemberships().map(membership => membership.member);
  }

  fullMemberEmails(): string[] {
    return this.getEmails(this.fullMembers());
  }

  findMemberBy(key, value) {
    const filtered = this.members().filter(m => m !== undefined && m[key] === value);
    let member = null;
    if (filtered.length > 0) {
      member = filtered[0];
    }
    return member;
  }

  findFullMemberBy(key, value): IMemberModel {
    return this.fullMembers().find(member => member[key] === value);
  }

  getEmails(members: IMemberModel[]): string[] {
    return members.map(member => member.email);
  }

  findPendingMemberBy(key, value): IMemberModel {
    return this.pendingMembers().find(member => member[key] === value);
  }

  removeMembership({email, id}: {email?: string, id?: string}): void {
    let membership = null;
    if (email) {
      membership = this.findMembershipByEmail(email);
    } else {
      membership = this.memberships.find(m => m.id === id);
    }
    if (membership) {
      this.memberships = this.memberships.filter(m => m.id !== membership.id);
      this.groups.forEach(g => g.removeMembership(membership.email));
    }
  }

  moveDocument(doc: TOrganizationDocument, newParent): void {
    const oldParent = doc.parent;
    // don't move if trying to move to:
    // current location
    if (oldParent !== newParent) {
      store.dispatch('organizationStore/moveDocument', {organization: doc.organization, document: doc, newParent: newParent});
      oldParent.removeDocument(doc);
      newParent.addDocument(doc);
    }
  }

  findRole(id: string): Organization['roles'][0] {
    return this.roles.find(r => r.id === id);
  }

  removeRole(idOrRole: string | Organization['roles'][0]): void {
    const id = typeof idOrRole === 'string' ? idOrRole : idOrRole.id;
    const role = this.findRole(id);
    if (role) {
      this.roles.splice(this.roles.indexOf(role), 1);
    }
  }

  findQuickLinkFolder(id: string): Organization['quick_link_folders'][0] {
    return this.quick_link_folders.find(folder => folder.id === id);
  }

  findEntityType(entityTypeId: string): EntityType {
    return this.entity_types.find(r => r.id === entityTypeId);
  }

  findUserType(userTypeId: string): UserType {
    return this.user_types.find(r => r.id === userTypeId);
  }

  removeEntityType(entityTypeId: string): void {
    const entityType = this.findEntityType(entityTypeId);
    if (entityType) {
      this.entity_types.splice(this.entity_types.indexOf(entityType), 1);
    }
  }

  removeUserType(userTypeId: string): void {
    const userType = this.findUserType(userTypeId);
    if (userType) {
      this.user_types.splice(this.user_types.indexOf(userType), 1);
    }
  }

  removeQuickLinkFolder(quickLinkFolderId: string): void {
    const quickLinkFolder = this.findQuickLinkFolder(quickLinkFolderId);
    if (quickLinkFolder) {
      this.quick_link_folders.splice(this.quick_link_folders.indexOf(quickLinkFolder), 1);
    }
  }

  get rolesForMembers(): IOrganizationRole[] {
    return this.roles.filter(role => role.role_type === RoleType.organization && role.id !== this.organization_owner_role_id) as IOrganizationRole[];
  }

  get documentRoles(): IDocumentRole[] {
    return this.roles.filter(role => role.role_type === RoleType.document) as IDocumentRole[];
  }

  get peerDocumentRoles(): IPeerDocumentRole[] {
    return this.roles.filter(role => role.role_type === RoleType.peerDocument) as IPeerDocumentRole[];
  }

  get portalRoles(): IPortalRole[] {
    return this.roles.filter(role => role.role_type === RoleType.portal) as IPortalRole[];
  }

  get ownerRole(): Role {
    return this.roles.find(r => r.id === this.organization_owner_role_id);
  }

  get ownerRoleId(): string {
    return this.organization_owner_role_id;
  }

  get membershipOwner() {
    return this.memberships.find(m => m.role_ids.includes(this.ownerRoleId));
  }

  get roleManagementRoleId(): number {
    return OrganizationRoleCapability.manageRoles;
  }

  get organizationId() {
    return this.id;
  }

  ownerRoleIdForType(type: DocumentType): string {
    switch (type) {
      case DocumentType.portal:
        return this.portal_owner_role_id;
      default:
        return this.document_owner_role_id;
    }
  }

  get logo(): ImageUpload {
    return this.organization.image_uploads.find(img => img.name === 'logo');
  }

  get hasTemplates(): boolean {
    return this.templates.length > 0;
  }

  get url(): string {
    return RouteHelper.rootUrl({subdomain: this.subdomain});
  }

  get isPersonalForms(): boolean {
    return this.name === 'Personal Forms'
  }

  get isOrganization(): true {
    return true
  }

  get isGroup(): false {
    return false
  }
}
